/**
 * Entity Configurations - Single Source of Truth
 *
 * Simplified configuration for entity API fetching.
 * Used by pagination hooks and list pages.
 */

import type { TradingDroidApi } from "@tradingdroid/api-client";
import type { ActionCreatorWithPayload } from "@reduxjs/toolkit";

import {
  setBacktests,
  setExchanges,
  setExecutions,
  setJobs,
  setOptimizations,
  setStrategies,
} from "@src/services/state/dataReducer";

import type { RootState } from "@src/services/state/store";

// ============================================
// ENTITY CONFIGURATIONS
// ============================================

/**
 * Entity configurations - single source of truth for all data fetching behavior
 *
 * Uses individually exported action creators from dataReducer to preserve
 * proper typing without needing type assertions.
 */
export const ENTITY_CONFIGS = {
  // ========================================
  // Type A: Full-Set Entities
  // Small datasets that app needs completely loaded at startup
  // ========================================
  strategies: {
    apiMethod: "getStrategies",
    pageSize: 100,
    idField: "id",
    stateKey: "strategies",
    initAction: setStrategies,
  } satisfies EntityConfig,

  exchanges: {
    apiMethod: "getExchanges",
    pageSize: 100,
    idField: "name",
    stateKey: "exchanges",
    initAction: setExchanges,
  } satisfies EntityConfig,

  // ========================================
  // Type B: Paginated Entities
  // Large datasets loaded per-page on list views
  // ========================================
  backtests: {
    apiMethod: "getBacktests",
    pageSize: 10,
    idField: "id",
    stateKey: "backtests",
    initAction: setBacktests,
  } satisfies EntityConfig,

  executions: {
    apiMethod: "getExecutions",
    pageSize: 10,
    idField: "id",
    stateKey: "executions",
    initAction: setExecutions,
  } satisfies EntityConfig,

  studies: {
    apiMethod: "getStudies",
    pageSize: 10,
    idField: "study_id",
    stateKey: "optimizations",
    initAction: setOptimizations,
    embedParam: "job", // Always embed job metadata (exchange, symbol, dates, etc.)
  } satisfies EntityConfig,

  jobs: {
    apiMethod: "getJobs",
    pageSize: 50,
    idField: "id",
    stateKey: "jobs",
    initAction: setJobs,
  } satisfies EntityConfig,
};

// ============================================
// ENTITY CONFIGURATION INTERFACE
// ============================================

/**
 * Helper type to extract only array-typed keys from RootState['data']
 */
type DataArrayKeys = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [K in keyof RootState["data"]]: RootState["data"][K] extends any[] ? K : never;
}[keyof RootState["data"]];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface EntityConfig<T = any> {
  /** API client method name (e.g., 'getBacktests', 'getStrategies') */
  apiMethod: keyof TradingDroidApi;

  /** Page size - how many items to fetch per request */
  pageSize: number;

  /** ID field name (usually 'id', but 'study_id' for studies) */
  idField: string;

  /** Redux state path - only keys with array values */
  stateKey: DataArrayKeys;

  /** Redux action for setting items */
  initAction: ActionCreatorWithPayload<T[]>;

  /** Optional embed parameter for API calls (e.g., 'job' for studies) */
  embedParam?: string;
}

// ============================================
// HELPER FUNCTION
// ============================================

/** Type alias for valid entity names */
export type EntityName = keyof typeof ENTITY_CONFIGS;

/**
 * Get entity configuration
 *
 * @param entityName - The entity name (e.g., 'backtests', 'executions')
 */
// Overloads to provide narrower return types for specific entities (so callers
// that check the entity name may access entity-specific properties without casting)
export function getEntityConfig(entityName: "studies"): (typeof ENTITY_CONFIGS)["studies"];
export function getEntityConfig<K extends Exclude<EntityName, "studies">>(
  entityName: K,
): (typeof ENTITY_CONFIGS)[K];
export function getEntityConfig<K extends EntityName>(entityName: K): (typeof ENTITY_CONFIGS)[K] {
  const entityConfig = ENTITY_CONFIGS[entityName];

  if (!entityConfig) {
    throw new Error(`Entity config not found for: ${entityName}`);
  }

  return entityConfig;
}
